"use client";

import Image from "next/image";
import { IBM_Plex_Sans_Thai, Poppins, JetBrains_Mono } from "next/font/google";
import type { ReactNode } from "react";
import "./seaaii-auth.css";

// Brand faces from the design mockups, self-hosted via next/font and exposed
// to the scoped CSS through CSS variables (see seaaii-auth.css).
const ibm = IBM_Plex_Sans_Thai({ subsets: ["thai", "latin"], weight: ["400", "500", "600", "700"], variable: "--font-ibm", display: "swap" });
const poppins = Poppins({ subsets: ["latin"], weight: ["600", "700"], variable: "--font-poppins", display: "swap" });
const mono = JetBrains_Mono({ subsets: ["latin"], weight: ["500", "700"], variable: "--font-mono", display: "swap" });

const AUTH_LOGO = "/seaaii/assets/img/auth-logo.png";

// Shared chrome for every Seaaii auth screen: decorative background, header
// (logo + language + website link), and a centered <main>. `alignEnd` pushes
// the content to the right on wide screens (login / register, which sit beside
// the LINE QR panel).
export function SeaaiiAuthFrame({ children, alignEnd = false }: { children: ReactNode; alignEnd?: boolean }) {
  return (
    <div className={`sa-auth ${ibm.variable} ${poppins.variable} ${mono.variable}${alignEnd ? " align-end" : ""}`}>
      <div className="bg" aria-hidden="true">
        <div className="dots" />
        <div className="ring" />
        <div className="ring small" />
        <svg className="spark" width="40" height="40" viewBox="0 0 40 40" fill="currentColor"><path d="M20 2c1.2 7.5 4.5 10.8 16 18-11.5 7.2-14.8 10.5-16 18-1.2-7.5-4.5-10.8-16-18C15.5 12.8 18.8 9.5 20 2Z" /></svg>
        <svg className="waves" viewBox="0 0 760 660" fill="none" preserveAspectRatio="xMinYMax meet">
          <path d="M-40 360 C 140 250 250 470 430 420 C 560 384 600 560 760 520 L760 700 L-40 700 Z" fill="#dfe5f8" opacity=".7" />
          <path d="M-40 470 C 120 380 260 540 430 510 C 580 484 620 620 760 600 L760 700 L-40 700 Z" fill="#d2dbf5" opacity=".8" />
          <path d="M-40 560 C 150 500 250 620 440 600 C 590 584 640 660 760 650 L760 700 L-40 700 Z" fill="#c6d1f1" />
        </svg>
      </div>

      <div className="shell">
        <header>
          <a className="brand" href="https://seaaii.com">
            {/* logo-full.png carries both the mark and the wordmark. */}
            <Image src="/seaaii/assets/img/logo-full.png" alt="Seaaii" width={440} height={163} priority style={{ height: 34, width: "auto", objectFit: "contain" }} />
          </a>
          <div className="top-right">
            <button type="button" className="lang">
              <span>ไทย</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
            </button>
            <div className="sep" />
            <a className="site-link" href="https://seaaii.com">
              <span>ไปยังเว็บไซต์</span>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6" /><path d="M10 14 21 3" /><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /></svg>
            </a>
          </div>
        </header>
        <main>{children}</main>
      </div>
    </div>
  );
}

// The bird mark shown at the top of each card.
export function SaCardLogo() {
  return <Image src={AUTH_LOGO} alt="" width={325} height={267} className="card-logo" priority style={{ width: 64, height: 64, objectFit: "contain" }} />;
}

// LINE QR support panel shown beside login / register.
export function SaQrPanel() {
  return (
    <aside className="qr-panel">
      <div className="qr-box">
        <Image src="/seaaii/assets/img/line-qr.jpg" alt="LINE QR @seaaii" width={540} height={540} />
      </div>
      <p className="qr-cap">
        แสกน QR Code หรือเพิ่มเพื่อน LINE <b style={{ color: "var(--ink-soft)" }}>@seaaii</b>
        <br />สำหรับปรึกษา พูดคุยกับแอดมิน
      </p>
    </aside>
  );
}

// Password show/hide toggle (eye icons matching the design).
export function SaEye({ shown, onToggle }: { shown: boolean; onToggle: () => void }) {
  return (
    <button type="button" className="eye" onClick={onToggle} tabIndex={-1} aria-label={shown ? "ซ่อนรหัสผ่าน" : "แสดงรหัสผ่าน"}>
      {shown ? (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M9.9 4.2A10.9 10.9 0 0 1 12 4c6.5 0 10 7 10 7a18 18 0 0 1-3.2 4.2M6.6 6.6A18 18 0 0 0 2 11s3.5 7 10 7a10.9 10.9 0 0 0 4.4-.9" /><path d="M3 3l18 18" /></svg>
      ) : (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></svg>
      )}
    </button>
  );
}

// Leading field icons.
export const SaIcons = {
  mail: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2.5" /><path d="m3 7 9 6 9-6" /></svg>,
  lock: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="11" width="16" height="10" rx="2.5" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>,
  user: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></svg>,
  store: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="3" width="11" height="18" rx="1.5" /><path d="M15 8h4a1.5 1.5 0 0 1 1.5 1.5V21" /><path d="M7.5 7h3M7.5 11h3M7.5 15h3" /></svg>,
  phone: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 3h3.5l1.5 5-2 1.5a13 13 0 0 0 6 6l1.5-2 5 1.5V20a2 2 0 0 1-2 2A17 17 0 0 1 3 5a2 2 0 0 1 2-2Z" /></svg>,
  tag: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20.6 11.4 12.6 3.4a2 2 0 0 0-1.4-.6H5a2 2 0 0 0-2 2v6.2a2 2 0 0 0 .6 1.4l8 8a2 2 0 0 0 2.8 0l6.2-6.2a2 2 0 0 0 0-2.8Z" /><circle cx="8" cy="8" r="1.4" /></svg>,
};
